/**
 * El router del panel — ADR-0012, paso 2.
 *
 * QUÉ CAMBIA Y QUÉ NO
 * El árbol de rutas es EXACTAMENTE el mismo (`AppRoutes`, sin tocar una línea): lo único que cambia
 * es cómo se monta. Se pasa del API declarativo (`<BrowserRouter><Routes>`) al **router de datos**
 * (`createBrowserRouter`), que es el que habilita declarar la carga en la RUTA con `loader`.
 *
 * ⭐ POR QUÉ ESTE PASO ES EL QUE FALTABA
 * Con `<Routes>`, los datos SÓLO pueden pedirse después de montar el componente: no hay dónde
 * declararlos antes. Por eso el waterfall del panel no era un descuido repetido 203 veces — era lo
 * único que ese API permite. El router de datos arranca la carga cuando arranca la navegación, en
 * paralelo con la descarga del chunk de la ruta.
 *
 * 🪤 ESTE ARCHIVO NO AGREGA NINGÚN `loader`, A PROPÓSITO.
 * Cambiar el árbol de rutas es el punto más sensible de la app —ya rompió producción dos veces por
 * otras causas (ver `vite.chunks.ts`)— y un cambio de montaje que además mueve la carga de datos
 * mezcla dos riesgos en un mismo despliegue: si algo falla, no se sabe cuál de los dos fue. Primero
 * se convierte el montaje y se verifica que TODO sigue igual; los `loader` se migran después, de a
 * una ruta. Una ruta sin `loader` se comporta exactamente como hoy.
 *
 * 🪤 El router se construye UNA vez, a nivel de módulo. Construirlo dentro del componente lo
 * recrearía en cada render y React Router perdería el estado de navegación en cada uno.
 */

import { createBrowserRouter, createRoutesFromElements, Route, Outlet } from 'react-router-dom';
import { FeatureFlagsProvider } from '@smarty/shared';
import { AutoSyncManager, LastNonSettingsLocationTracker } from '@/routes/rootWidgets';
import { LegacyDomainGate } from '@/components/LegacyDomainGate';
import { PanelRumProbe } from '@/components/analytics/PanelRumProbe';
import { AppRoutes } from '@/routes/AppRoutes';

/**
 * Todo lo que antes vivía ENTRE `<BrowserRouter>` y `<Routes>`.
 *
 * 🪤 Con `RouterProvider` no se puede meter nada entre el router y las rutas: lo que necesite estar
 * adentro del router tiene que ser el elemento de una ruta. De ahí esta capa raíz — no es
 * ceremonia, es el único lugar donde estos componentes siguen viendo la navegación.
 *
 * Se monta una vez y no se vuelve a montar al navegar, así que `AutoSyncManager` y la sonda de RUM
 * conservan su estado entre pantallas, igual que antes.
 */
function CapaRaiz() {
  return (
    <FeatureFlagsProvider>
      <AutoSyncManager />
      <LastNonSettingsLocationTracker />
      {/* Mide clic → contenido visible (ADR-0012 paso 5). Necesita el router (para ver la ruta) y
          la caché (para saber si hubo consultas); las dos siguen estando arriba. */}
      <PanelRumProbe />
      <LegacyDomainGate>
        <Outlet />
      </LegacyDomainGate>
    </FeatureFlagsProvider>
  );
}

/**
 * El árbol de rutas, tal cual, colgando de la capa raíz.
 *
 * `createRoutesFromElements` acepta el mismo JSX que usaba `<Routes>`, así que las 330 rutas no se
 * reescriben: se reinterpretan. Reescribirlas a objetos habría sido un diff de miles de líneas
 * imposible de revisar, y el objetivo de este paso es que el comportamiento no cambie.
 */
export const rutas = createRoutesFromElements(
  <Route element={<CapaRaiz />}>{AppRoutes}</Route>,
);

/**
 * 🪤 `v7_startTransition` NO va acá. En react-router 6.30 ese flag es de `RouterProvider`, no del
 * router: `createBrowserRouter` sólo acepta los que cambian el comportamiento de RUTAS
 * (`v7_relativeSplatPath`, y los de `loader`/`action`/`fetcher`). Ponerlo acá es un error de tipos,
 * no un aviso — y si se silenciara con un cast, el flag simplemente no haría nada.
 *
 * Los flags de `loader`/`action`/`fetcher` (`v7_fetcherPersist`, `v7_normalizeFormMethod`,
 * `v7_partialHydration`, `v7_skipActionErrorRevalidation`) se activan cuando se migre la primera
 * ruta con datos: hoy no habría nada que cambiaran.
 */
export const router = createBrowserRouter(rutas, {
  future: {
    v7_relativeSplatPath: true,
  },
});
